// Owner-cockpit API client.
// Hits POST /api/v1/owner/cockpit/<tool>. Auth is a Bearer JWT pasted on the
// login page and persisted in localStorage (v1 — replaceable later with a
// proper OAuth/email-link flow without changing this surface).

import {
  ApiError,
  type AssistantChatRequest,
  type AssistantChatResult,
  type ChannelAttributionResult,
  type LostRevenueResult,
  type MarketingSegmentsResult,
  type OccupancyResult,
  type PresignRequest,
  type PresignResponse,
  type PublicBookingState,
  type PublicBookingUpdate,
  type RangeRequest,
  type RevenueResult,
  type SegmentPreviewResult,
  type SendWinbackRequest,
  type SendWinbackResult,
} from './types'
import { mock } from './mock'


// --- LINE rich menu (Level 2) -----------------------------------------------

export interface RichMenuAction {
  type: 'portal' | 'chat' | 'url'
  tab?: string
  url?: string
}

export interface RichMenuCustomConfig {
  image_url: string
  layout: string
  actions: RichMenuAction[]
  chat_bar_text?: string | null
}

export interface RichMenuStatus {
  line_connected: boolean
  bot_name: string | null
  bot_basic_id: string | null
  rich_menu_provisioned: boolean
  rich_menu_id: string | null
  provisioned_at: string | null
  rich_menu_mode: 'generated' | 'custom'
  custom_config: RichMenuCustomConfig | null
}

export interface RichMenuZone {
  x: number
  y: number
  width: number
  height: number
}

export interface RichMenuLayoutDef {
  id: string
  width: number
  height: number
  zones: RichMenuZone[]
}

export interface RichMenuLayoutsResponse {
  layouts: RichMenuLayoutDef[]
  portal_tabs: string[]
  max_image_bytes: number
}

const USE_MOCK = (import.meta.env.VITE_USE_MOCK as string | undefined) === 'true'
const API_ROOT = (import.meta.env.VITE_API_BASE || '') + '/api/v1'
const BASE = `${API_ROOT}/owner/cockpit`

const TOKEN_KEY = 'petpro_owner_token'
const REFRESH_KEY = 'petpro_owner_refresh'

export const auth = {
  get token(): string | null {
    return localStorage.getItem(TOKEN_KEY)
  },
  get refresh(): string | null {
    return localStorage.getItem(REFRESH_KEY)
  },
  set: (value: string) => localStorage.setItem(TOKEN_KEY, value.trim()),
  setRefresh: (value: string) => localStorage.setItem(REFRESH_KEY, value.trim()),
  setPair: (access: string, refresh: string) => {
    localStorage.setItem(TOKEN_KEY, access.trim())
    localStorage.setItem(REFRESH_KEY, refresh.trim())
  },
  clear: () => {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_KEY)
  },
  isMock: () => USE_MOCK,
}

// Single-flight refresh: if multiple requests fire 401 at once, only one
// /auth/refresh call actually runs; the rest await the same promise.
let _refreshInFlight: Promise<string | null> | null = null

async function refreshAccessToken(): Promise<string | null> {
  const r = auth.refresh
  if (!r) return null
  if (_refreshInFlight) return _refreshInFlight
  _refreshInFlight = (async () => {
    try {
      const resp = await fetch(`${API_ROOT}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: r }),
      })
      if (!resp.ok) {
        // Refresh token itself is invalid/expired. Clear both tokens
        // so the next request fails fast → bounces to /login (instead
        // of perpetually retrying with a doomed refresh).
        if (resp.status === 401 || resp.status === 403) auth.clear()
        return null
      }
      const j = (await resp.json()) as { access_token?: string; refresh_token?: string }
      if (!j.access_token) return null
      auth.set(j.access_token)
      if (j.refresh_token) auth.setRefresh(j.refresh_token)
      return j.access_token
    } catch {
      return null
    } finally {
      // Clear the in-flight slot AFTER the body returns so concurrent
      // awaiters all get the same answer.
      setTimeout(() => {
        _refreshInFlight = null
      }, 0)
    }
  })()
  return _refreshInFlight
}

async function request<T>(
  url: string,
  opts: { method: string; body?: unknown; _retried?: boolean },
): Promise<T> {
  const t = auth.token
  const init: RequestInit = {
    method: opts.method,
    headers: {
      'Content-Type': 'application/json',
      ...(t ? { Authorization: `Bearer ${t}` } : {}),
    },
  }
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body)
  let resp: Response
  try {
    resp = await fetch(url, init)
  } catch {
    throw new ApiError(0, 'errors.network')
  }
  if (!resp.ok) {
    // On 401, try to refresh exactly ONCE before giving up.
    if (resp.status === 401 && !opts._retried && auth.refresh) {
      const fresh = await refreshAccessToken()
      if (fresh) return request<T>(url, { ...opts, _retried: true })
    }
    let detail: unknown = ''
    try {
      const j = (await resp.json()) as { detail?: unknown }
      detail = j?.detail ?? ''
    } catch {
      /* non-JSON */
    }
    // Phase 6C — 402 carries the subscription-block payload (a JSON
    // object, not a string). Surface a global 'subscription:blocked'
    // event so the app shell can refresh state + show the paywall.
    if (resp.status === 402 && typeof detail === 'object' && detail !== null) {
      try {
        window.dispatchEvent(
          new CustomEvent('subscription:blocked', { detail }),
        )
      } catch {
        /* SSR or test env without window */
      }
      const msg = (detail as { error?: string }).error || 'subscription_blocked'
      throw new ApiError(resp.status, msg)
    }
    if (resp.status === 401 || resp.status === 403) {
      throw new ApiError(resp.status, 'errors.unauthorized')
    }
    throw new ApiError(
      resp.status,
      typeof detail === 'string' && detail ? detail : 'errors.generic',
    )
  }
  return (await resp.json()) as T
}

async function post<T>(path: string, body: unknown): Promise<T> {
  return request<T>(`${BASE}${path}`, { method: 'POST', body })
}

export const api = {
  occupancy: (r: RangeRequest): Promise<OccupancyResult> =>
    USE_MOCK ? mock.occupancy(r) : post('/occupancy', r),
  revenue: (r: RangeRequest): Promise<RevenueResult> =>
    USE_MOCK ? mock.revenue(r) : post('/revenue', r),
  channelAttribution: (r: RangeRequest): Promise<ChannelAttributionResult> =>
    USE_MOCK ? mock.channelAttribution(r) : post('/channel-attribution', r),
  lostRevenue: (r: RangeRequest): Promise<LostRevenueResult> =>
    USE_MOCK ? mock.lostRevenue(r) : post('/lost-revenue', r),

  marketingSegments: (): Promise<MarketingSegmentsResult> =>
    USE_MOCK ? mock.marketingSegments() : post('/marketing/segments', {}),
  previewSegment: (segment_type: string, filters?: Record<string, unknown>): Promise<SegmentPreviewResult> =>
    USE_MOCK
      ? mock.previewSegment(segment_type, filters)
      : post('/marketing/preview-segment', { segment_type, filters }),
  sendWinback: (body: SendWinbackRequest): Promise<SendWinbackResult> =>
    USE_MOCK ? mock.sendWinback(body) : post('/marketing/send-winback', body),

  assistantChat: (body: AssistantChatRequest): Promise<AssistantChatResult> =>
    USE_MOCK ? mock.assistantChat(body) : post('/assistant/chat', body),

  getPublicBooking: (): Promise<PublicBookingState> =>
    USE_MOCK
      ? mock.getPublicBooking()
      : request<PublicBookingState>(`${API_ROOT}/clinic/public-booking`, { method: 'GET' }),
  updatePublicBooking: (body: PublicBookingUpdate): Promise<PublicBookingState> =>
    USE_MOCK
      ? mock.updatePublicBooking(body)
      : request<PublicBookingState>(`${API_ROOT}/clinic/public-booking`, {
          method: 'PUT',
          body,
        }),

  // Phase 6A.5 — get a presigned PUT URL, then upload directly to R2.
  // Returns the public URL the caller stores in clinic settings.
  uploadClinicAsset: async (kind: 'logo' | 'gallery' | 'rich_menu', file: File): Promise<string> => {
    if (USE_MOCK) return mock.uploadClinicAsset(kind, file)
    const presign: PresignResponse = await request(
      `${API_ROOT}/clinic/uploads/presigned`,
      {
        method: 'POST',
        body: {
          kind,
          filename: file.name,
          content_type: file.type,
          size_bytes: file.size,
        } satisfies PresignRequest,
      },
    )
    const put = await fetch(presign.upload_url, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    })
    if (!put.ok) {
      throw new ApiError(put.status, 'errors.uploadFailed')
    }
    return presign.public_url
  },

  // LINE rich menu (generated + Level-2 custom artwork)
  richMenu: {
    status: (): Promise<RichMenuStatus> =>
      USE_MOCK
        ? mock.richMenuStatus()
        : request(`${API_ROOT}/boarding/line/rich-menu/status`, { method: 'GET' }),
    layouts: (): Promise<RichMenuLayoutsResponse> =>
      USE_MOCK
        ? mock.richMenuLayouts()
        : request(`${API_ROOT}/boarding/line/rich-menu/layouts`, { method: 'GET' }),
    saveCustom: (cfg: RichMenuCustomConfig): Promise<{ saved: boolean; zones: number }> =>
      USE_MOCK
        ? mock.richMenuSaveCustom(cfg)
        : request(`${API_ROOT}/boarding/line/rich-menu/custom`, { method: 'PUT', body: cfg }),
    provision: (mode: 'generated' | 'custom'): Promise<{ success: boolean }> =>
      USE_MOCK
        ? mock.richMenuProvision(mode)
        : request(`${API_ROOT}/boarding/line/rich-menu/provision`, {
            method: 'POST',
            body: { mode },
          }),
    unprovision: (): Promise<{ success: boolean }> =>
      USE_MOCK
        ? mock.richMenuUnprovision()
        : request(`${API_ROOT}/boarding/line/rich-menu`, { method: 'DELETE' }),
    // Template download needs the auth header, so fetch → blob → object URL.
    downloadTemplate: async (layout: string): Promise<Blob> => {
      if (USE_MOCK) return mock.richMenuTemplate()
      const t = auth.token
      const resp = await fetch(
        `${API_ROOT}/boarding/line/rich-menu/template?layout=${encodeURIComponent(layout)}`,
        { headers: t ? { Authorization: `Bearer ${t}` } : {} },
      )
      if (!resp.ok) throw new ApiError(resp.status, 'errors.generic')
      return resp.blob()
    },
  },
}

// ---------------------------------------------------------------------------
// Phase 6C — subscription status (always reachable; gate-allowlisted)
// ---------------------------------------------------------------------------

export interface SubscriptionStatus {
  status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'suspended' | 'comped' | 'deleted' | null
  effective_status: SubscriptionStatus['status']
  tier: string | null
  paid_through_at: string | null
  trial_ends_at: string | null
  grace_days_remaining: number  // -1 = no grace context
  is_demo: boolean
  comp_source_invite_code_id?: string | null
}

export interface SubscriptionPayment {
  id: string
  amount: string
  currency: string
  method: string
  period_start: string
  period_end: string
  tier_at_payment: string
  notes: string | null
  created_at: string
}

// --- Concierge policy KB (智慧客服 店家政策/FAQ) ---------------------------

export interface KbEntry {
  id: string
  question: string
  answer: string
  tags: string[]
  is_active: boolean
}

const MOCK_KB: KbEntry[] = [
  { id: 'kb-1', question: '可以帶自己的飼料嗎？', answer: '可以喔！入住時請附上餵食說明，我們會按您的指示餵食。', tags: [], is_active: true },
  { id: 'kb-2', question: '取消政策', answer: '入住前 3 天可免費取消；3 天內取消收取一晚費用。', tags: [], is_active: true },
]

export const concierge = {
  listKb(): Promise<KbEntry[]> {
    return USE_MOCK
      ? Promise.resolve([...MOCK_KB])
      : request<KbEntry[]>(`${API_ROOT}/concierge/kb`, { method: 'GET' })
  },
  createKb(body: { question: string; answer: string }): Promise<KbEntry> {
    return USE_MOCK
      ? Promise.resolve({ id: `kb-${Date.now()}`, tags: [], is_active: true, ...body })
      : request<KbEntry>(`${API_ROOT}/concierge/kb`, { method: 'POST', body })
  },
  updateKb(id: string, body: Partial<Pick<KbEntry, 'question' | 'answer' | 'is_active'>>): Promise<KbEntry> {
    return USE_MOCK
      ? Promise.resolve({ id, question: '', answer: '', tags: [], is_active: true, ...body } as KbEntry)
      : request<KbEntry>(`${API_ROOT}/concierge/kb/${id}`, { method: 'PUT', body })
  },
  deleteKb(id: string): Promise<void> {
    return USE_MOCK
      ? Promise.resolve()
      : request<{ deleted: boolean }>(`${API_ROOT}/concierge/kb/${id}`, { method: 'DELETE' }).then(() => undefined)
  },
}

export const billing = {
  async getSubscriptionStatus(): Promise<SubscriptionStatus> {
    return request<SubscriptionStatus>(`${API_ROOT}/billing/subscription/status`, {
      method: 'GET',
    })
  },
  async listSubscriptionPayments(): Promise<SubscriptionPayment[]> {
    return request<SubscriptionPayment[]>(
      `${API_ROOT}/billing/subscription/payments`,
      { method: 'GET' },
    )
  },
  /** Phase 6C — Stripe Checkout for a chosen tier. Returns the Stripe URL
   * to redirect to. Throws ApiError(503) when Stripe isn't configured
   * (the SPA hides the button + shows bank-transfer instead).
   *
   * Pricing-layer extension (h8i9j0k1l2m3): `billingCycle` selects
   * monthly vs annual; `discountCode` carries a pre-validated promo.
   */
  async startStripeCheckout(
    tier: string,
    opts?: {
      billingCycle?: 'monthly' | 'annual'
      discountCode?: string
      product?: string
    },
  ): Promise<{ url: string; session_id: string }> {
    const origin = window.location.origin
    const body: Record<string, unknown> = {
      product: opts?.product ?? 'vetos',
      tier,
      billing_cycle: opts?.billingCycle ?? 'monthly',
      success_url: `${origin}/settings/billing?stripe=success`,
      cancel_url: `${origin}/settings/billing?stripe=cancel`,
    }
    if (opts?.discountCode) body.discount_code = opts.discountCode
    return request<{ url: string; session_id: string }>(
      `${API_ROOT}/billing/subscription/stripe/checkout`,
      { method: 'POST', body },
    )
  },

  /** Pricing-layer (h8i9j0k1l2m3) — server-driven tier price feed.
   * The Checkout picker pulls this on mount so amounts always match
   * what Stripe actually charges. Annual entries are hidden when the
   * corresponding STRIPE_PRICE_<product>_<tier>_ANNUAL env var is
   * unset on the backend (graceful degradation for the VetOS-only
   * annual launch). */
  async getSubscriptionPricing(product: string = 'vetos'): Promise<SubscriptionPricing> {
    return request<SubscriptionPricing>(
      `${API_ROOT}/billing/subscription/pricing?product=${encodeURIComponent(product)}`,
      { method: 'GET' },
    )
  },

  // --- Prepaid credit packages (Phase A1a — read-only catalog) ---

  async listPackages(opts?: {
    include_archived?: boolean
    service_type?: 'boarding' | 'daycare' | 'grooming'
  }): Promise<{ items: ServicePackage[] }> {
    if (USE_MOCK) return mock.listPackages(opts) as unknown as Promise<{ items: ServicePackage[] }>
    const q = new URLSearchParams()
    if (opts?.include_archived) q.set('include_archived', 'true')
    if (opts?.service_type) q.set('service_type', opts.service_type)
    const qs = q.toString() ? `?${q}` : ''
    return request<{ items: ServicePackage[] }>(
      `${API_ROOT}/merchant/me/packages${qs}`,
      { method: 'GET' },
    )
  },

  async getPackage(id: string): Promise<ServicePackage> {
    return request<ServicePackage>(
      `${API_ROOT}/merchant/me/packages/${encodeURIComponent(id)}`,
      { method: 'GET' },
    )
  },

  async createPackage(body: ServicePackageCreatePayload): Promise<ServicePackage> {
    if (USE_MOCK)
      return mock.createPackage(body as unknown as Record<string, unknown>) as unknown as Promise<ServicePackage>
    return request<ServicePackage>(`${API_ROOT}/merchant/me/packages`, {
      method: 'POST',
      body,
    })
  },

  async updatePackage(
    id: string,
    body: Partial<ServicePackageCreatePayload>,
  ): Promise<ServicePackage> {
    if (USE_MOCK)
      return mock.updatePackage(id, body as unknown as Record<string, unknown>) as unknown as Promise<ServicePackage>
    return request<ServicePackage>(
      `${API_ROOT}/merchant/me/packages/${encodeURIComponent(id)}`,
      { method: 'PATCH', body },
    )
  },

  async archivePackage(id: string): Promise<ServicePackage> {
    if (USE_MOCK) return mock.archivePackage(id) as unknown as Promise<ServicePackage>
    return request<ServicePackage>(
      `${API_ROOT}/merchant/me/packages/${encodeURIComponent(id)}/archive`,
      { method: 'POST' },
    )
  },

  async restorePackage(id: string): Promise<ServicePackage> {
    if (USE_MOCK) return mock.restorePackage(id) as unknown as Promise<ServicePackage>
    return request<ServicePackage>(
      `${API_ROOT}/merchant/me/packages/${encodeURIComponent(id)}/restore`,
      { method: 'POST' },
    )
  },

  async listPackagePurchases(opts?: {
    status?: string
    expiring_within_days?: number
    package_id?: string
    limit?: number
  }): Promise<{ items: PackagePurchase[]; summary: { total_face_value_minor: number; count: number } }> {
    if (USE_MOCK)
      return mock.listPackagePurchases(opts) as unknown as Promise<{
        items: PackagePurchase[]
        summary: { total_face_value_minor: number; count: number }
      }>
    const q = new URLSearchParams()
    if (opts?.status) q.set('status', opts.status)
    if (opts?.expiring_within_days != null)
      q.set('expiring_within_days', String(opts.expiring_within_days))
    if (opts?.package_id) q.set('package_id', opts.package_id)
    if (opts?.limit) q.set('limit', String(opts.limit))
    const qs = q.toString() ? `?${q}` : ''
    return request<{
      items: PackagePurchase[]
      summary: { total_face_value_minor: number; count: number }
    }>(`${API_ROOT}/merchant/me/packages/purchases${qs}`, { method: 'GET' })
  },

  async getRefundPreview(purchaseId: string): Promise<RefundPreview> {
    if (USE_MOCK)
      return mock.getRefundPreview(purchaseId) as unknown as Promise<RefundPreview>
    return request<RefundPreview>(
      `${API_ROOT}/merchant/me/packages/purchases/${encodeURIComponent(purchaseId)}/refund-preview`,
      { method: 'GET' },
    )
  },

  async listRedemptions(opts?: {
    include_voided?: boolean
    limit?: number
  }): Promise<{ items: PackageRedemption[] }> {
    if (USE_MOCK)
      return mock.listRedemptions(opts) as unknown as Promise<{
        items: PackageRedemption[]
      }>
    const q = new URLSearchParams()
    if (opts?.include_voided) q.set('include_voided', 'true')
    if (opts?.limit) q.set('limit', String(opts.limit))
    const qs = q.toString() ? `?${q}` : ''
    return request<{ items: PackageRedemption[] }>(
      `${API_ROOT}/merchant/me/packages/redemptions${qs}`,
      { method: 'GET' },
    )
  },

  async voidRedemption(
    redemptionId: string,
    body: { reason: string },
  ): Promise<{ id: string; is_voided: boolean; voided_at: string | null }> {
    if (USE_MOCK)
      return mock.voidRedemption(redemptionId, body) as unknown as Promise<{
        id: string
        is_voided: boolean
        voided_at: string | null
      }>
    return request<{
      id: string
      is_voided: boolean
      voided_at: string | null
    }>(
      `${API_ROOT}/merchant/me/packages/redemptions/${encodeURIComponent(redemptionId)}/void`,
      { method: 'POST', body },
    )
  },

  async restoreCredit(body: {
    original_redemption_id: string
    sessions_to_restore: number
    reason: string
  }): Promise<{
    id: string
    purchase_id: string
    compensates_redemption_id: string
    sessions_restored: number
  }> {
    if (USE_MOCK)
      return mock.restoreCredit(body) as unknown as Promise<{
        id: string
        purchase_id: string
        compensates_redemption_id: string
        sessions_restored: number
      }>
    return request<{
      id: string
      purchase_id: string
      compensates_redemption_id: string
      sessions_restored: number
    }>(`${API_ROOT}/merchant/me/packages/redemptions/restore-credit`, {
      method: 'POST',
      body,
    })
  },

  async getPackagesPeriodSummary(opts?: {
    from_date?: string
    to_date?: string
  }): Promise<{
    sold_count: number
    sold_revenue_minor: number
    expiring_within_7d: number
  }> {
    if (USE_MOCK)
      return mock.getPackagesPeriodSummary(opts) as unknown as Promise<{
        sold_count: number
        sold_revenue_minor: number
        expiring_within_7d: number
      }>
    const q = new URLSearchParams()
    if (opts?.from_date) q.set('from_date', opts.from_date)
    if (opts?.to_date) q.set('to_date', opts.to_date)
    const qs = q.toString() ? `?${q}` : ''
    return request<{
      sold_count: number
      sold_revenue_minor: number
      expiring_within_7d: number
    }>(`${API_ROOT}/merchant/me/packages/period-summary${qs}`, {
      method: 'GET',
    })
  },

  async getTierPriceChangeImpact(
    packageId: string,
    tierId: string,
  ): Promise<{
    tier_id: string
    active_purchases_on_old_price: number
    pending_checkout_count: number
    last_sale_at: string | null
    days_since_last_sale: number | null
  }> {
    if (USE_MOCK)
      return mock.getTierPriceChangeImpact(
        packageId,
        tierId,
      ) as unknown as Promise<{
        tier_id: string
        active_purchases_on_old_price: number
        pending_checkout_count: number
        last_sale_at: string | null
        days_since_last_sale: number | null
      }>
    return request<{
      tier_id: string
      active_purchases_on_old_price: number
      pending_checkout_count: number
      last_sale_at: string | null
      days_since_last_sale: number | null
    }>(
      `${API_ROOT}/merchant/me/packages/${encodeURIComponent(packageId)}/tiers/${encodeURIComponent(tierId)}/price-change-impact`,
      { method: 'GET' },
    )
  },

  // --- ADDONS-2: booking add-on catalog CRUD ---
  async listAddOns(opts?: {
    active_only?: boolean
    category?: string
    service_type?: 'boarding' | 'daycare' | 'grooming'
  }): Promise<AddonService[]> {
    if (USE_MOCK)
      return mock.listAddOns(opts) as unknown as Promise<AddonService[]>
    const q = new URLSearchParams()
    if (opts?.active_only !== undefined)
      q.set('active_only', String(opts.active_only))
    if (opts?.category) q.set('category', opts.category)
    if (opts?.service_type) q.set('service_type', opts.service_type)
    const qs = q.toString() ? `?${q}` : ''
    return request<AddonService[]>(
      `${API_ROOT}/boarding/addon-services${qs}`,
      { method: 'GET' },
    )
  },

  async createAddOn(body: AddonServiceCreate): Promise<AddonService> {
    if (USE_MOCK)
      return mock.createAddOn(
        body as unknown as Record<string, unknown>,
      ) as unknown as Promise<AddonService>
    return request<AddonService>(`${API_ROOT}/boarding/addon-services`, {
      method: 'POST',
      body,
    })
  },

  async updateAddOn(
    id: string,
    body: AddonServiceUpdate,
  ): Promise<AddonService> {
    if (USE_MOCK)
      return mock.updateAddOn(
        id,
        body as unknown as Record<string, unknown>,
      ) as unknown as Promise<AddonService>
    return request<AddonService>(
      `${API_ROOT}/boarding/addon-services/${encodeURIComponent(id)}`,
      { method: 'PATCH', body },
    )
  },

  async deleteAddOn(id: string): Promise<void> {
    if (USE_MOCK) return mock.deleteAddOn(id) as unknown as Promise<void>
    return request<void>(
      `${API_ROOT}/boarding/addon-services/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    )
  },

  async refundPurchase(
    purchaseId: string,
    body: { amount_minor: number; reason: string; request_uuid: string },
  ): Promise<{
    purchase: { id: string; status: string; amount_refunded_minor: number }
    stripe_refund_id: string
    refunded_amount_minor: number
  }> {
    if (USE_MOCK)
      return mock.refundPurchase(purchaseId, body) as unknown as Promise<{
        purchase: { id: string; status: string; amount_refunded_minor: number }
        stripe_refund_id: string
        refunded_amount_minor: number
      }>
    return request<{
      purchase: { id: string; status: string; amount_refunded_minor: number }
      stripe_refund_id: string
      refunded_amount_minor: number
    }>(`${API_ROOT}/merchant/me/packages/purchases/${encodeURIComponent(purchaseId)}/refund`, {
      method: 'POST',
      body,
    })
  },

  /** Pricing-layer (h8i9j0k1l2m3) — pre-validate a promo code BEFORE
   * the user clicks Pay so the FE can show inline savings. Backend
   * collapses all "not usable" outcomes into one generic
   * ERROR_DISCOUNT_INVALID (enumeration defense) — surfacing as
   * ApiError 404. */
  async validatePromoCode(
    code: string,
    product: string,
    tier: string,
    cycle: 'monthly' | 'annual',
  ): Promise<PromoPreview> {
    const params = new URLSearchParams({ code, product, tier, cycle })
    return request<PromoPreview>(
      `${API_ROOT}/billing/subscription/promo-validate?${params.toString()}`,
      { method: 'GET' },
    )
  },
}

// Pricing-layer response shapes.
export interface SubscriptionPricing {
  product: string
  tiers: PricingTier[]
  active_override: {
    tier: string
    billing_cycle: string
    amount: number
    end_date: string
  } | null
}

/** Bilingual feature bullet — admin writes both languages in one
 * PATCH, frontend picks based on i18next active locale. */
export interface PricingFeature {
  en: string
  zh: string
}

export interface PricingTier {
  tier: string
  currency: string
  monthly_amount: number | null
  annual_amount?: number
  annual_monthly_equivalent?: number
  features?: PricingFeature[]
}

// --- Prepaid credit packages (Phase A1a; read-only catalog) ---

export interface ServicePackageTier {
  id: string
  paid_sessions: number
  bonus_sessions: number
  price_minor: number
  label_en: string | null
  label_zh: string | null
  is_default: boolean
  stripe_price_id: string | null
  display_order: number
  is_active: boolean
}

// Write payload — slim variant of ServicePackage suitable for POST/PATCH.
export interface ServicePackageTierPayload {
  paid_sessions: number
  bonus_sessions?: number
  price_minor: number
  label_en?: string | null
  label_zh?: string | null
  is_default?: boolean
  display_order?: number
}

export interface ServicePackageCreatePayload {
  service_type: 'boarding' | 'daycare' | 'grooming'
  name_en: string
  name_zh: string
  description_en?: string | null
  description_zh?: string | null
  base_price_minor: number
  currency?: string
  validity_days?: number | null
  transferability?: 'household' | 'owner_only'
  refund_policy_mode?: 'none' | 'prorated' | 'prorated_with_fee' | 'full_within_window'
  refund_window_days?: number | null
  refund_unit_value_minor?: number | null
  refund_unit_value_basis?: 'paid' | 'total'
  refund_early_termination_fee_minor?: number
  einvoice_required?: boolean
  display_order?: number
  tiers: ServicePackageTierPayload[]
}

export interface PackagePurchase {
  id: string
  package_id: string
  snapshot_name_en: string
  snapshot_name_zh: string
  snapshot_service_type: 'boarding' | 'daycare' | 'grooming'
  sessions_total: number
  sessions_used: number
  sessions_remaining: number
  amount_paid_minor: number
  amount_refunded_minor: number
  currency: string
  status:
    | 'pending_payment'
    | 'active'
    | 'exhausted'
    | 'expired'
    | 'refunded'
    | 'cancelled'
    | 'disputed'
  boarding_owner_id: string | null
  purchased_at: string | null
  expires_at: string | null
}

export interface PackageRedemption {
  id: string
  purchase_id: string
  booking_kind: 'boarding' | 'daycare' | 'grooming'
  booking_id: string
  snapshot_service_type: 'boarding' | 'daycare' | 'grooming'
  redemption_kind: 'redeem' | 'restore'
  sessions_used: number
  unit_value_minor_at_redemption: number
  redeemed_at: string
  is_voided: boolean
  voided_at: string | null
  void_reason: string | null
  compensates_redemption_id: string | null
}

export interface AddonService {
  id: string
  clinic_id: string
  name: string
  name_zh: string
  category: 'grooming' | 'wellness' | 'retail' | 'other' | string
  price: string  // Decimal serialized as string
  pricing_type: 'per_session' | 'per_night' | 'one_time' | string
  duration_minutes: number | null
  is_active: boolean
  display_order: number
  notes: string | null
  applicable_service_types: Array<'boarding' | 'daycare' | 'grooming'>
  created_at: string
  updated_at: string
}

export interface AddonServiceCreate {
  name: string
  name_zh: string
  category?: string
  price: string | number
  pricing_type?: string
  duration_minutes?: number | null
  is_active?: boolean
  display_order?: number
  notes?: string | null
  applicable_service_types?: Array<'boarding' | 'daycare' | 'grooming'>
}

export interface AddonServiceUpdate {
  name?: string
  name_zh?: string
  category?: string
  price?: string | number
  pricing_type?: string
  duration_minutes?: number | null
  is_active?: boolean
  display_order?: number
  notes?: string | null
  applicable_service_types?: Array<'boarding' | 'daycare' | 'grooming'>
}

export interface RefundPreview {
  allowed_minor: number
  amount_paid_minor: number
  amount_refunded_minor: number
  stripe_fee_minor: number
  sessions_used: number
  snapshot_refund_unit_value_minor: number
  snapshot_refund_policy_mode: 'none' | 'prorated' | 'prorated_with_fee' | 'full_within_window'
  snapshot_refund_unit_value_basis: 'paid' | 'total'
}

export interface ServicePackage {
  id: string
  clinic_id: string
  service_type: 'boarding' | 'daycare' | 'grooming'
  name_en: string
  name_zh: string
  description_en: string | null
  description_zh: string | null
  base_price_minor: number
  currency: string
  validity_days: number | null
  transferability: 'household' | 'owner_only'
  refund_policy_mode: 'none' | 'prorated' | 'prorated_with_fee' | 'full_within_window'
  refund_window_days: number | null
  refund_unit_value_minor: number | null
  refund_unit_value_basis: 'paid' | 'total'
  refund_early_termination_fee_minor: number
  size_tier_constraint: string | null
  stripe_product_id: string | null
  einvoice_required: boolean
  display_order: number
  is_active: boolean
  archived_at: string | null
  created_at: string
  updated_at: string
  tiers: ServicePackageTier[]
}

export interface PromoPreview {
  valid: true
  discount_kind: 'percent' | 'amount'
  discount_value: number
  duration: 'once' | 'forever' | 'repeating'
  duration_count: number | null
  preview: {
    base_amount: number
    discount_amount: number
    net_amount: number
    currency: string
  }
}

// ---------------------------------------------------------------------------
// Phase 6B — owner login API (LINE callback / claim-code redeem / magic link)
// ---------------------------------------------------------------------------

export interface PendingClaimProfile {
  line_sub: string
  name: string | null
  email: string | null
  picture: string | null
}

export interface OwnerLoginTokens {
  access_token: string
  refresh_token: string
  is_new: boolean
}

export const ownerAuth = {
  /** URL the SPA navigates to in order to start LINE OAuth. The browser
   * lands on access.line.me, then bounces back to /auth/owner/line/callback
   * which 302s to /auth/callback#access_token=…&refresh_token=… (handled
   * by AuthCallback.tsx). */
  lineStartUrl(): string {
    return `${API_ROOT}/auth/owner/line/start`
  },

  /** Read the (non-sensitive) profile fields from the pending-claim cookie
   * so the /login/claim screen can show "Welcome, {name}". 401 if the
   * cookie is missing or expired. Cookies travel cross-origin only when
   * the API + SPA share a registrable domain — in our split deploy
   * (api.vetos.pet + owner.petpro.com.tw) this endpoint can't be called
   * directly; LoginClaim.tsx falls back to "Welcome" generic copy. */
  async pendingClaim(): Promise<PendingClaimProfile | null> {
    try {
      const resp = await fetch(`${API_ROOT}/auth/owner/pending-claim`, {
        credentials: 'include',
      })
      if (!resp.ok) return null
      return (await resp.json()) as PendingClaimProfile
    } catch {
      return null
    }
  },

  /** POST the 6-digit claim code; on success returns the token pair.
   * Cookie credentials must be included so the backend can read the
   * owner_pending_claim ticket cookie set during the LINE callback. */
  async claim(code: string): Promise<OwnerLoginTokens> {
    const resp = await fetch(`${API_ROOT}/auth/owner/claim`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: code.trim() }),
    })
    if (!resp.ok) {
      let detail = 'errors.generic'
      try {
        const j = (await resp.json()) as { detail?: string }
        if (j.detail) detail = j.detail
      } catch {
        /* ignore */
      }
      throw new ApiError(resp.status, detail)
    }
    return (await resp.json()) as OwnerLoginTokens
  },

  /** Request a magic link. Returns the cooldown the server set; the
   * actual delivery is opaque (anti-enumeration). */
  async requestMagicLink(email: string): Promise<{ ok: boolean; cooldown_seconds: number }> {
    const resp = await fetch(`${API_ROOT}/auth/owner/magic-link/request`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim() }),
    })
    if (!resp.ok) throw new ApiError(resp.status, 'errors.generic')
    return (await resp.json()) as { ok: boolean; cooldown_seconds: number }
  },
}
